export const classifyScan = (raw: string, config: ScanClassifierConfig = {}): ScanClassification => {
  const value = raw.trim();
  const commands = config.commands === false ? {} : config.commands ?? defaultCommands;
  const command = Object.keys(commands).find(name => commands[name].some(alias => alias.trim().toLowerCase() === value.toLowerCase())) ?? null;
  const eanFormat = /^\d{8}$/.test(value) ? 'EAN-8' : /^\d{13}$/.test(value) ? 'EAN-13' : null;
  const ean: ScanClassification['ean'] = eanFormat ? { format: eanFormat, valid: isValidEan(value) } : null;
  const numericValue = value && /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(value) && Number.isFinite(Number(value)) ? Number(value) : null;
  const kind: ScanKind = !value ? 'empty' : command ? 'command' : eanFormat === 'EAN-8' ? 'ean8' : eanFormat === 'EAN-13' ? 'ean13' : numericValue != null ? 'number' : 'text';
  const matches = (config.rules ?? []).filter(rule => rule.test(value)).map(rule => rule.name);
  return { raw, value, kind, command: value ? command : null, ean, numericValue, matches };
};

export const isValidEan = (value: string): boolean => {
  if (!/^(?:\d{8}|\d{13})$/.test(value)) return false;
  const digits = value.slice(0, -1);
  const sum = Array.from(digits).reduce((total, digit, index) => total + Number(digit) * ((digits.length - index) % 2 === 1 ? 3 : 1), 0);
  return (10 - sum % 10) % 10 === Number(value[value.length - 1]);
};

export const findScanMatches = <Row,>({ rows, value, getIdentifiers, mode = 'exact', caseSensitive = true }: ScanMatchConfig<Row>): Row[] => {
  const normalize = (input: unknown) => { const text = String(input ?? '').trim(); return caseSensitive ? text : text.toLowerCase(); };
  const term = normalize(value);
  if (!term) return [];
  return rows.filter(row => getIdentifiers(row).some(identifier => {
    const candidate = normalize(identifier);
    return candidate !== '' && (mode === 'contains' ? candidate.includes(term) : candidate === term);
  }));
};

export type ScanKind = 'empty' | 'command' | 'ean8' | 'ean13' | 'number' | 'text';
export type ScanClassification = { raw: string; value: string; kind: ScanKind; command: string | null; ean: { format: 'EAN-8' | 'EAN-13'; valid: boolean } | null; numericValue: number | null; matches: string[] };
export type ScanClassifierConfig = { commands?: Readonly<Record<string, readonly string[]>> | false; rules?: readonly { name: string; test: (value: string) => boolean }[] };
export type ScanMatchConfig<Row> = { rows: readonly Row[]; value: string; getIdentifiers: (row: Row) => readonly unknown[]; mode?: 'exact' | 'contains'; caseSensitive?: boolean };

const defaultCommands = { ok: ['OK'], cancel: ['CANCEL'] };
